import React, { useState } from 'react';
import './JobPage.css';
import {
  Search,
  MapPin,
  Briefcase,
  DollarSign,
  Clock,
  Building,
  Filter,
  ChevronRight,
  Bookmark,
  Share2,
  CheckCircle,
  X,
  Star,
  TrendingUp,
  Users,
  Target,
  BarChart3,
  ExternalLink,
  AlertCircle
} from 'lucide-react';

const JobPage = () => {
  const [activeTab, setActiveTab] = useState('recommended');
  const [savedJobs, setSavedJobs] = useState([2, 4]);
  const [showFilters, setShowFilters] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('');

  const toggleSavedJob = (jobId) => {
    if (savedJobs.includes(jobId)) {
      setSavedJobs(savedJobs.filter(id => id !== jobId));
    } else {
      setSavedJobs([...savedJobs, jobId]);
    }
  };

  const jobs = [
    {
      id: 1,
      title: 'Senior Frontend Developer',
      company: 'Meta',
      location: 'San Francisco, CA',
      type: 'Full-time',
      salary: '$140,000 - $180,000',
      experience: '5+ years',
      posted: '2 days ago',
      applicants: 124,
      skills: ['React', 'TypeScript', 'Next.js', 'GraphQL'],
      description: 'We are looking for a Senior Frontend Developer to join our team. You will be responsible for building user interfaces...',
      remote: true,
      urgent: false
    },
    {
      id: 2,
      title: 'Product Manager',
      company: 'Google',
      location: 'New York, NY',
      type: 'Full-time',
      salary: '$150,000 - $200,000',
      experience: '6+ years',
      posted: '1 day ago',
      applicants: 89,
      skills: ['Product Strategy', 'Agile', 'Data Analysis', 'UX'],
      description: 'Lead product development initiatives and work with cross-functional teams to deliver amazing user experiences...',
      remote: true,
      urgent: true
    },
    {
      id: 3,
      title: 'Data Scientist',
      company: 'Netflix',
      location: 'Los Angeles, CA',
      type: 'Full-time',
      salary: '$160,000 - $210,000',
      experience: '4+ years',
      posted: '3 days ago',
      applicants: 210,
      skills: ['Python', 'Machine Learning', 'SQL', 'TensorFlow'],
      description: 'Join our data science team to build recommendation systems and analyze user behavior patterns...',
      remote: false,
      urgent: false
    },
    {
      id: 4,
      title: 'UX Designer',
      company: 'Apple',
      location: 'Cupertino, CA',
      type: 'Full-time',
      salary: '$130,000 - $170,000',
      experience: '3+ years',
      posted: '5 days ago',
      applicants: 156,
      skills: ['Figma', 'Prototyping', 'User Research', 'UI Design'],
      description: 'Design intuitive and beautiful user interfaces for our next generation of products...',
      remote: true,
      urgent: false
    },
    {
      id: 5,
      title: 'DevOps Engineer',
      company: 'Amazon Web Services',
      location: 'Seattle, WA',
      type: 'Full-time',
      salary: '$145,000 - $190,000',
      experience: '5+ years',
      posted: '1 week ago',
      applicants: 95,
      skills: ['AWS', 'Kubernetes', 'Terraform', 'Docker'],
      description: 'Build and maintain our cloud infrastructure and CI/CD pipelines...',
      remote: true,
      urgent: true
    },
    {
      id: 6,
      title: 'Backend Engineer',
      company: 'Spotify',
      location: 'Stockholm, Sweden',
      type: 'Full-time',
      salary: '€90,000 - €120,000',
      experience: '4+ years',
      posted: '2 days ago',
      applicants: 78,
      skills: ['Node.js', 'Python', 'PostgreSQL', 'Redis'],
      description: 'Develop scalable backend services for our music streaming platform...',
      remote: true,
      urgent: false
    }
  ];

  const recommendedJobs = jobs.slice(0, 4);
  const recentJobs = jobs.slice(2);

  const companies = [
    { id: 1, name: 'Microsoft', jobs: 342, hiring: true, logo: 'MS' },
    { id: 2, name: 'Tesla', jobs: 189, hiring: true, logo: 'TS' },
    { id: 3, name: 'Salesforce', jobs: 256, hiring: true, logo: 'SF' },
    { id: 4, name: 'Adobe', jobs: 167, hiring: false, logo: 'AD' }
  ];

  const skills = [
    { name: 'React', demand: 'High', jobs: 12500 },
    { name: 'Python', demand: 'Very High', jobs: 23400 },
    { name: 'AWS', demand: 'High', jobs: 18900 },
    { name: 'Data Science', demand: 'Very High', jobs: 15600 }
  ];

  return (
    <div className="job-page">
      {/* Header */}
      <div className="jobs-header">
        <div className="jobheader-content">
          <div className="header-top">
            <h1 className="jobpage-title">Jobs</h1>
            <div className="header-stats">
              <div className="stat-item">
                <Users size={20} />
                <span>Manage applications</span>
              </div>
              <div className="stat-item">
                <Bookmark size={20} />
                <span>{savedJobs.length} saved jobs</span>
              </div>
              <div className="stat-item">
                <Target size={20} />
                <span>Job alerts</span>
              </div>
            </div>
          </div>
          <div className="jobsearch-container">
            <div className="search-section">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Search jobs, companies, or keywords"
                className="jobsearch-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="search-section">
              <MapPin className="search-icon" size={20} />
              <input
                type="text"
                placeholder="City, state, or zip code"
                className="jobsearch-input"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
            </div>
            <button className="search-btn">
              <Search size={20} />
              <span>Search</span>
            </button>
            <button className="filter-toggle" onClick={() => setShowFilters(!showFilters)}>
              <Filter size={20} />
              <span>Filters</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="jobs-tabs">
            <button 
              className={`job-tab ${activeTab === 'recommended' ? 'active' : ''}`}
              onClick={() => setActiveTab('recommended')}
            >
              Recommended for you
              <span className="tab-count">{recommendedJobs.length}</span>
            </button>
            <button 
              className={`job-tab ${activeTab === 'recent' ? 'active' : ''}`}
              onClick={() => setActiveTab('recent')}
            >
              Recent searches
              <span className="tab-count">{recentJobs.length}</span>
            </button>
            <button 
              className={`job-tab ${activeTab === 'saved' ? 'active' : ''}`}
              onClick={() => setActiveTab('saved')}
            >
              Saved jobs
              <span className="tab-count">{savedJobs.length}</span>
            </button>
            <button 
              className={`job-tab ${activeTab === 'applied' ? 'active' : ''}`}
              onClick={() => setActiveTab('applied')}
            >
              Applied jobs
              <span className="tab-count">3</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="filters-panel">
          <div className="filter-section">
            <h3 className="filter-title">Date Posted</h3>
            <div className="filter-options">
              <label className="filter-option">
                <input type="checkbox" />
                <span>Past 24 hours</span>
              </label>
              <label className="filter-option">
                <input type="checkbox" />
                <span>Past week</span>
              </label>
              <label className="filter-option">
                <input type="checkbox" />
                <span>Past month</span>
              </label>
            </div>
          </div>
          <div className="filter-section">
            <h3 className="filter-title">Job Type</h3>
            <div className="filter-options">
              <label className="filter-option">
                <input type="checkbox" />
                <span>Full-time</span>
              </label>
              <label className="filter-option">
                <input type="checkbox" />
                <span>Part-time</span>
              </label>
              <label className="filter-option">
                <input type="checkbox" />
                <span>Contract</span>
              </label>
              <label className="filter-option">
                <input type="checkbox" />
                <span>Remote</span>
              </label>
            </div>
          </div>
          <div className="filter-section">
            <h3 className="filter-title">Experience Level</h3>
            <div className="filter-options">
              <label className="filter-option">
                <input type="checkbox" />
                <span>Entry level</span>
              </label>
              <label className="filter-option">
                <input type="checkbox" />
                <span>Mid-Senior level</span>
              </label>
              <label className="filter-option">
                <input type="checkbox" />
                <span>Director</span>
              </label>
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn-secondary">Clear all</button>
            <button className="btn-primary">Apply filters</button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="jobs-main-content">
        <div className="jobs-container">
          {/* Left Column - Job Listings */}
          <div className="jobs-left-column">
            {/* Job Stats */}
            <div className="job-stats-card">
              <div className="stat-highlight">
                <TrendingUp size={24} />
                <div>
                  <h3>Jobs where you're a top applicant</h3>
                  <p>Based on your profile and search history</p>
                </div>
              </div>
              <div className="stat-numbers">
                <div className="stat-number">
                  <strong>42</strong>
                  <span>Total matches</span>
                </div>
                <div className="stat-number">
                  <strong>18</strong>
                  <span>In your network</span>
                </div>
                <div className="stat-number">
                  <strong>7</strong>
                  <span>Easy Apply</span>
                </div>
              </div>
            </div>

            {/* Job Listings */}
            <div className="job-listings">
              {jobs.map(job => (
                <div key={job.id} className={`job-card ${job.urgent ? 'urgent' : ''}`}>
                  {job.urgent && (
                    <div className="urgent-badge">
                      <AlertCircle size={14} />
                      <span>Urgently hiring</span>
                    </div>
                  )}
                  
                  <div className="job-card-header">
                    <div className="job-info">
                      <div className="company-logo">
                        {job.company.charAt(0)}
                      </div>
                      <div className="job-details">
                        <h3 className="job-title">{job.title}</h3>
                        <div className="company-name">{job.company}</div>
                        <div className="job-meta">
                          <span className="meta-item">
                            <MapPin size={14} />
                            {job.location}
                          </span>
                          <span className="meta-item">
                            <Briefcase size={14} />
                            {job.type}
                          </span>
                          {job.remote && (
                            <span className="tag remote">Remote</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="job-actions">
                      <button 
                        className={`save-btn ${savedJobs.includes(job.id) ? 'saved' : ''}`}
                        onClick={() => toggleSavedJob(job.id)}
                      >
                        <Bookmark size={20} />
                      </button>
                      <button className="share-btn">
                        <Share2 size={20} />
                      </button>
                    </div>
                  </div>

                  <div className="job-description">
                    <p>{job.description}</p>
                  </div>

                  <div className="job-salary">
                    <DollarSign size={16} />
                    <span>{job.salary}</span>
                    <span className="separator">•</span>
                    <Clock size={16} />
                    <span>{job.posted}</span>
                    <span className="separator">•</span>
                    <Users size={16} />
                    <span>{job.applicants} applicants</span>
                  </div>

                  <div className="job-skills">
                    {job.skills.map((skill, index) => (
                      <span key={index} className="jobskill-tag">{skill}</span>
                    ))}
                  </div>

                  <div className="job-footer">
                    <button className="btn-apply">
                      <CheckCircle size={18} />
                      Easy Apply
                    </button>
                    <button className="btn-view">
                      <ExternalLink size={18} />
                      View job
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Sidebar */}
          <div className="jobs-right-column">
            {/* Profile Completeness */}
            <div className="sidebar-card">
              <div className="card-header">
                <h3 className="jobcard-title">Your profile is 85% complete</h3>
              </div>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: '85%' }}></div>
              </div>
              <p className="card-text">Complete your profile to get better job recommendations</p>
              <button className="btn-outline">Complete profile</button>
            </div>

            {/* Top Companies Hiring */}
            <div className="sidebar-card">
              <div className="card-header">
                <h3 className="jobcard-title">Top companies hiring now</h3>
                <button className="see-all">See all</button>
              </div>
              <div className="companies-list">
                {companies.map(company => (
                  <div key={company.id} className="company-item">
                    <div className="company-logo-small">
                      {company.logo}
                    </div>
                    <div className="company-details">
                      <h4 className="company-name">{company.name}</h4>
                      <div className="company-meta">
                        <span>{company.jobs} jobs</span>
                        {company.hiring && <span className="hiring-badge">Hiring</span>}
                      </div>
                    </div>
                    <ChevronRight size={20} className="chevron" />
                  </div>
                ))}
              </div>
            </div>

            {/* In-Demand Skills */}
            <div className="sidebar-card">
              <div className="card-header">
                <h3 className="jobcard-title">In-demand skills</h3>
                <button className="see-all">See all</button>
              </div>
              <div className="skills-list">
                {skills.map((skill, index) => (
                  <div key={index} className="skill-item">
                    <div className="skill-info">
                      <h4 className="skill-name">{skill.name}</h4>
                      <div className="skill-meta">
                        <span className={`demand ${skill.demand.toLowerCase().replace(' ', '-')}`}>
                          {skill.demand} demand
                        </span>
                        <span>{skill.jobs.toLocaleString()} jobs</span>
                      </div>
                    </div>
                    <BarChart3 size={20} className="chart-icon" />
                  </div>
                ))}
              </div>
            </div>

            {/* Job Alert */}
            <div className="sidebar-card alert-card">
              <div className="card-header">
                <h3 className="jobcard-title">Set up job alerts</h3>
              </div>
              <p className="card-text">Get notified when new jobs match your search</p>
              <div className="alert-form">
                <input 
                  type="text" 
                  placeholder="Job title or keyword" 
                  className="alert-input"
                />
                <button className="btn-primary">Create alert</button>
              </div>
            </div>

            {/* Salary Insights */}
            <div className="sidebar-card">
              <div className="card-header">
                <h3 className="jobcard-title">Salary insights</h3>
              </div>
              <div className="salary-item">
                <h4>Frontend Developer</h4>
                <div className="salary-range">
                  <span>$110K</span>
                  <span className="separator">-</span>
                  <span>$160K</span>
                </div>
                <div className="salary-source">Based on 1,234 salaries</div>
              </div>
              <div className="salary-item">
                <h4>Product Manager</h4>
                <div className="salary-range">
                  <span>$130K</span>
                  <span className="separator">-</span>
                  <span>$190K</span>
                </div>
                <div className="salary-source">Based on 987 salaries</div>
              </div>
              <button className="btn-text">Explore salaries</button>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="jobs-footer">
        <div className="footer-content">
          <p className="footer-text">
            <Star size={16} />
            <span>UniCircle predicts you'll get a response if you apply</span>
          </p>
          <div className="footer-links">
            <a href="#" className="footer-link">About</a>
            <a href="#" className="footer-link">Accessibility</a>
            <a href="#" className="footer-link">Help Center</a>
            <a href="#" className="footer-link">Privacy & Terms</a>
            <a href="#" className="footer-link">Ad Choices</a>
          </div>
          <div className="footer-copyright">
            UniCircle Corporation © 2026
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobPage;